import { zxcvbnOptions } from '@zxcvbn-ts/core';
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en';
import { marked, Renderer } from 'marked';
import SanitizeHtml from 'sanitize-html';

var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// constants/index.ts
var constants_exports = {};
__export(constants_exports, {
  Offering: () => offerings_exports
});

// constants/offerings.ts
var offerings_exports = {};
__export(offerings_exports, {
  COMMON_STOCK_TYPE: () => COMMON_STOCK_TYPE,
  CONVERTIBLE_NOTE_STOCK_TYPE: () => CONVERTIBLE_NOTE_STOCK_TYPE,
  EXEMPTION_TYPES: () => EXEMPTION_TYPES,
  IN_LIEU_OF_CASH_STOCK_TYPE: () => IN_LIEU_OF_CASH_STOCK_TYPE,
  PREFERRED_STOCK_TYPE: () => PREFERRED_STOCK_TYPE,
  REGULATIONS: () => REGULATIONS,
  REGULATION_A: () => REGULATION_A,
  REGULATION_A_PLUS: () => REGULATION_A_PLUS,
  REGULATION_CF: () => REGULATION_CF,
  REGULATION_D: () => REGULATION_D,
  REGULATION_S: () => REGULATION_S,
  RULES: () => RULES,
  RULE_504B: () => RULE_504B,
  RULE_506B: () => RULE_506B,
  RULE_506C: () => RULE_506C,
  SAFE_STOCK_TYPE: () => SAFE_STOCK_TYPE,
  SECTION_4A5: () => SECTION_4A5,
  SELF_UNDERWRITTEN: () => SELF_UNDERWRITTEN,
  STATUS: () => STATUS,
  StockTypeMap: () => StockTypeMap,
  TIERS: () => TIERS,
  TIER_1: () => TIER_1,
  TIER_2: () => TIER_2,
  UNIT_STOCK_TYPE: () => UNIT_STOCK_TYPE
});
var REGULATION_A = "Regulation A";
var REGULATION_A_PLUS = "Regulation A+";
var REGULATION_D = "Regulation D";
var REGULATION_S = "Regulation S";
var REGULATION_CF = "Regulation CF";
var SECTION_4A5 = "Section 4(a)(5)";
var SELF_UNDERWRITTEN = "Self-Underwritten";
var RULE_504B = "Rule 504(b)";
var RULE_506B = "Rule 506(b)";
var RULE_506C = "Rule 506(c)";
var TIER_1 = "Tier 1";
var TIER_2 = "Tier 2";
var EXEMPTION_TYPES = {
  REG_A: "REG_A",
  REG_A_PLUS: "REG_A_PLUS",
  REG_D_504_B: "REG_D_504_B",
  REG_D_506_B: "REG_D_506_B",
  REG_D_506_C: "REG_D_506_C",
  REG_S: "REG_S",
  REGULATION_CF: "REGULATION_CF",
  SECTION_4A5: "SECTION_4A5",
  SELF_UNDERWRITTEN: "SELF_UNDERWRITTEN"
};
var TIERS = {
  TIER_1,
  TIER_2
};
var RULES = {
  RULE_504B,
  RULE_506B,
  RULE_506C
};
var REGULATIONS = {
  REGULATION_A,
  REGULATION_A_PLUS,
  REGULATION_D,
  REGULATION_S,
  REGULATION_CF
};
var STATUS = {
  DRAFT: "DRAFT",
  IN_REVIEW: "IN_REVIEW",
  ACTIVE: "ACTIVE",
  CLOSED: "CLOSED",
  TERMINATED: "TERMINATED"
};
var COMMON_STOCK_TYPE = "common_stock";
var CONVERTIBLE_NOTE_STOCK_TYPE = "convertible_note";
var PREFERRED_STOCK_TYPE = "preferred_stock";
var UNIT_STOCK_TYPE = "unit";
var SAFE_STOCK_TYPE = "safe";
var IN_LIEU_OF_CASH_STOCK_TYPE = "in_lieu_of_cash";
var StockTypeMap = {
  [COMMON_STOCK_TYPE]: "Common Stock",
  [CONVERTIBLE_NOTE_STOCK_TYPE]: "Convertible Notes",
  [PREFERRED_STOCK_TYPE]: "Preferred Stock",
  [UNIT_STOCK_TYPE]: "Units",
  [SAFE_STOCK_TYPE]: "SAFE",
  [IN_LIEU_OF_CASH_STOCK_TYPE]: "In Lieu of Cash"
};

// constants/roles.ts
var FULL_ROLES = {
  INVESTOR: "investor",
  ADMIN: "admin",
  OWNER: "owner",
  EDITOR: "editor",
  VIEWER: "viewer"
};
var ROLES = {
  ADMIN: "admin",
  OWNER: "owner",
  EDITOR: "editor",
  VIEWER: "viewer"
};
var DEFAULT_ROLES = {
  OWNER: "owner",
  EDITOR: "editor",
  VIEWER: "viewer"
};
var ROLE_OPTIONS = [
  { value: ROLES.OWNER, label: "Owner" },
  { value: ROLES.EDITOR, label: "Editor" },
  { value: ROLES.VIEWER, label: "Viewer" }
];

// constants/markdown.ts
var AllowedMarkdownElements = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "p",
  "span",
  "ul",
  "ol",
  "li",
  "strong",
  "em",
  "blockquote",
  "hr",
  "br",
  "a"
];

// constants/agreement.ts
var PENDING = "PENDING";
var SIGNED = "SIGNED";
var COMPLETED = "COMPLETED";
var VOIDED = "VOIDED";
var CANCELED = "CANCELED";
var AGREEMENT_STATUSES = {
  PENDING,
  SIGNED,
  COMPLETED,
  VOIDED,
  CANCELED
};

// constants/inquiries.ts
var INQUIRY_STATUS_VARIANT = {
  pending: {
    text: "Pending",
    variant: "pending"
  },
  success: {
    text: "Approved",
    variant: "success"
  },
  error: {
    text: "Failed",
    variant: "error"
  },
  created: {
    text: "Started",
    variant: "unknown"
  },
  unknown: {
    text: "Not Started",
    variant: "unknown"
  }
};

// helpers/offerings.ts
var OfferingDisplayStatus = {
  DRAFT: "Draft",
  ACTIVE: "Active",
  IN_REVIEW: "In Review",
  CLOSED: "Closed",
  TERMINATED: "Terminated"
};
var offeringIsRegD = (offering) => {
  return [
    EXEMPTION_TYPES.REG_D_506_B,
    EXEMPTION_TYPES.REG_D_506_C,
    EXEMPTION_TYPES.REG_D_504_B
  ].includes(offering?.exemption_type);
};
var offeringDisplayStatus = (status) => {
  if (!status) return "";
  return OfferingDisplayStatus[status] ?? "";
};
var offeringIsRegA = (offering) => {
  return offering?.exemption_type === EXEMPTION_TYPES.REG_A;
};
var offeringIsRegAPlus = (offering) => {
  return offering?.exemption_type === EXEMPTION_TYPES.REG_A_PLUS;
};
var offeringIsTypeRegA = (offering) => {
  return offeringIsRegA(offering) || offeringIsRegAPlus(offering);
};
var offeringIsRule506c = (offering) => {
  return offering?.exemption_type === EXEMPTION_TYPES.REG_D_506_C;
};
var offeringIsRule506b = (offering) => {
  return offering?.exemption_type === EXEMPTION_TYPES.REG_D_506_B;
};
var offeringIsActive = (offering) => {
  return offering?.status === STATUS.ACTIVE;
};
var offeringIsTerminated = (offering) => {
  return offering?.status === STATUS.TERMINATED;
};
var offeringIsClosed = (offering) => {
  return offering?.status === STATUS.CLOSED;
};
var offeringIsCompleted = (offering) => {
  return offeringIsClosed(offering) || offeringIsTerminated(offering);
};
var offeringIsUnitType = (offering) => {
  return offering?.stock_type === UNIT_STOCK_TYPE;
};
var offeringHasNoAccreditation = (offering) => {
  return offeringIsRegA(offering) && !offering?.collect_additional_info || offeringIsRule506b(offering);
};

// helpers/users.ts
var map = {
  initial: (value) => value ? `${value.substring(0, 1)}.` : null,
  full: (value) => value ?? null,
  none: () => null
};
var userFullName = (user, options = {}) => {
  const middle = map[options?.middle ?? "full"]?.(user?.middle_name);
  return [user?.first_name, middle, user?.last_name].filter(Boolean).filter((s) => typeof s === "string").map((s) => s.trim()).filter(Boolean).join(" ");
};
var initializeZxcvbn = () => {
  const options = {
    translations: zxcvbnEnPackage.translations,
    graphs: zxcvbnCommonPackage.adjacencyGraphs,
    dictionary: {
      ...zxcvbnCommonPackage.dictionary,
      ...zxcvbnEnPackage.dictionary
    }
  };
  zxcvbnOptions.setOptions(options);
};
var NumberFormatter = new Intl.NumberFormat("en-US");

// helpers/string.ts
var capitalizeEveryWord = (inputString) => {
  return inputString.replace(/\b\w+/g, (match) => {
    return match.charAt(0).toUpperCase() + match.slice(1).toLowerCase();
  });
};

// helpers/investments.ts
function calculateInvestmentFees(investment) {
  const { amount, payments, offering } = investment;
  const successfulPayments = payments.filter(
    (payment) => payment.status === "succeeded"
  );
  const appFeeTotal = successfulPayments.reduce((sum, payment) => {
    return sum + Number(payment.fee || 0);
  }, 0);
  const totalFeeSum = successfulPayments.reduce((sum, payment) => {
    return sum + Number(payment.total_fee || 0);
  }, 0);
  const stripeFeeTotal = totalFeeSum - appFeeTotal;
  const grossAmount = Number(amount || 0);
  const netAmount = grossAmount - totalFeeSum;
  const paymentType = successfulPayments[0]?.payment_type || "card";
  const feeRate = paymentType === "us_bank_account" ? Number(offering?.ach_fee || 0) * 100 : Number(offering?.credit_card_fee || 0) * 100;
  return {
    app_fee: appFeeTotal,
    app_fee_rate: feeRate,
    stripe_fee: stripeFeeTotal,
    net_amount: netAmount
  };
}
function investmentBcode(value) {
  if (!value) return null;
  const trimmedBcode = String(value || "").trim();
  return trimmedBcode || null;
}
function formatInvestmentFeeDisplay(fees) {
  return `$${fees.app_fee.toFixed(2)} (${fees.app_fee_rate.toFixed(2)}%)`;
}
var paymentTypeDisplay = (payment) => payment.payment_type === "us_bank_account" ? "US Bank Account" : payment.payment_type ? capitalizeEveryWord(payment.payment_type) : "Manual";
var investmentIsCompleted = (investment) => {
  if (!investment) return false;
  return investment?.status !== "PENDING";
};
var investmentDefaultEventHistoryTitle = (event, status) => {
  return [
    event === "KYC" ? "KYC" : capitalizeEveryWord(event),
    capitalizeEveryWord(status)
  ].join(" ");
};
var calculateMaxSubscriptionAmount = ({
  annualIncome,
  netWorth,
  investments = []
}) => {
  const income = Number.isNaN(Number(annualIncome)) ? 0 : Number(annualIncome);
  const worth = Number.isNaN(Number(netWorth)) ? 0 : Number(netWorth);
  const highest = Math.max(income, worth) ?? 0;
  const investedAmount = investments.reduce((acc, currentInv) => {
    if (currentInv.status !== "REJECTED") {
      return currentInv.amount + acc;
    }
    return acc;
  }, 0);
  const maxAmount = highest / 10 - investedAmount;
  return Math.max(maxAmount, 0);
};
var formatSecurities = ({
  quantity,
  offering,
  capitalize = false
}) => {
  const formattedQuantity = quantity && NumberFormatter.format(quantity);
  const isUnitType = offeringIsUnitType(offering);
  const formatText = (text) => {
    let result = text;
    if (!quantity || quantity > 1) result = `${text}s`;
    if (capitalize) result = capitalizeEveryWord(result);
    return result;
  };
  const label = isUnitType ? `${formatText("share")}/${formatText("unit")}` : `${formatText("share")}`;
  return {
    quantity: formattedQuantity,
    label,
    toString() {
      if (!formattedQuantity) return label;
      return `${formattedQuantity} ${label}`;
    }
  };
};
var headingStyles = {
  "font-family": "Figtree, Sans-serif",
  "font-weight": "700",
  color: "#000",
  "text-align": "inherit",
  "line-height": "1.2",
  "margin-bottom": "0.8em"
};
var emailStyles = {
  heading: headingStyles,
  h1: {
    ...headingStyles,
    "font-size": "22px",
    "margin-bottom": "40px"
  },
  h2: {
    ...headingStyles,
    "font-size": "18px"
  },
  h3: {
    ...headingStyles,
    "font-size": "16px"
  },
  h4: {
    ...headingStyles,
    "font-size": "14px"
  },
  h5: {
    ...headingStyles,
    "font-size": "12px"
  },
  h6: {
    ...headingStyles,
    "font-size": "10px"
  },
  paragraph: {
    "white-space-collapse": "preserve",
    "text-wrap-mode": "wrap",
    color: "#000",
    "line-height": "1.8",
    "font-size": "16px",
    "margin-bottom": "20px",
    "font-family": "Figtree, Sans-serif",
    "font-weight": "400",
    "text-align": "left"
  }
};
var objectToStyleString = (obj) => {
  return Object.entries(obj ?? {}).map(([key, value]) => `${key}:${value}`).join(";");
};
var EmailRenderer = () => {
  const renderer = new Renderer();
  const defaultParagraph = renderer.paragraph.bind(renderer);
  const defaultHeading = renderer.heading.bind(renderer);
  renderer.paragraph = (config) => {
    const style = objectToStyleString(emailStyles.paragraph);
    const paragraph = defaultParagraph(config);
    return paragraph.replace(/<p([^>]*)>/gi, `<p style="${style}"$1>`);
  };
  renderer.heading = (config) => {
    const style = objectToStyleString(
      emailStyles[`h${config.depth}`] || emailStyles.heading
    );
    const heading = defaultHeading(config);
    return heading.replace(/<h(\d)([^>])*>/gi, `<h$1 style="${style}" $2>`);
  };
  return renderer;
};
var Markdown;
((Markdown2) => {
  Markdown2.AllowedElements = AllowedMarkdownElements;
  Markdown2.useEmail = {
    renderer: EmailRenderer()
  };
  Markdown2.toHtml = (source, options = {}) => {
    const html = marked.parse(source, {
      async: false,
      ...options
    });
    return sanitizeHtml(html);
  };
  Markdown2.toHtmlAsync = async (source, options = {}) => {
    const html = await marked.parse(source, {
      async: true,
      ...options
    });
    return sanitizeHtml(html);
  };
  const sanitizeHtml = (html, options = {}) => {
    return SanitizeHtml(html, {
      allowedTags: Markdown2.AllowedElements,
      allowedAttributes: {
        a: ["href", "name", "style"],
        p: ["style"],
        h1: ["style"],
        h2: ["style"],
        h3: ["style"],
        ...options.allowedAttributes
      },
      ...options
    });
  };
})(Markdown || (Markdown = {}));

export { AGREEMENT_STATUSES, AllowedMarkdownElements, CANCELED, COMPLETED, constants_exports as Constants, DEFAULT_ROLES, FULL_ROLES, INQUIRY_STATUS_VARIANT, Markdown, OfferingDisplayStatus, PENDING, ROLES, ROLE_OPTIONS, SIGNED, VOIDED, calculateInvestmentFees, calculateMaxSubscriptionAmount, emailStyles, formatInvestmentFeeDisplay, formatSecurities, initializeZxcvbn, investmentBcode, investmentDefaultEventHistoryTitle, investmentIsCompleted, objectToStyleString, offeringDisplayStatus, offeringHasNoAccreditation, offeringIsActive, offeringIsClosed, offeringIsCompleted, offeringIsRegA, offeringIsRegAPlus, offeringIsRegD, offeringIsRule506b, offeringIsRule506c, offeringIsTerminated, offeringIsTypeRegA, offeringIsUnitType, paymentTypeDisplay, userFullName };
